/**
 * Smart routing for the applicant flow's "Connect an Email Provider" step.
 *
 * Given the applicant's saved application email, decide which verification
 * path to lead with:
 *   - "google"    → highlight Google OAuth as recommended
 *   - "microsoft" → highlight Microsoft OAuth as recommended
 *   - "otp"       → lead with the email-OTP path pre-filled to this address
 *   - null        → email is missing OR malformed; render fallback layout
 *
 * Malformed values (missing "@", empty local/domain, no dot in the domain)
 * are intentionally treated identically to absent values. No error UI fires
 * — the page just degrades to the today's neutral layout. See CLAUDE.md.
 */

export type EmailRoute = "google" | "microsoft" | "otp";

const GOOGLE_DOMAINS = new Set<string>([
  "gmail.com",
  "googlemail.com",
]);

// Microsoft consumer mail domains. A routing hint only: whether a Microsoft
// sign-in proves the email is decided by the server, from the same closed list.
// Keep the two identical when either changes.
const MICROSOFT_DOMAINS = new Set<string>([
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "outlook.co.uk",
  "hotmail.co.uk",
  "hotmail.fr",
  "live.co.uk",
]);

export function classifyEmail(email?: string | null): EmailRoute | null {
  if (!email) return null;
  const parts = email.trim().toLowerCase().split("@");
  if (parts.length !== 2) return null;
  const [local, domain] = parts;
  if (!local || !domain || !domain.includes(".")) return null;
  if (GOOGLE_DOMAINS.has(domain)) return "google";
  if (MICROSOFT_DOMAINS.has(domain)) return "microsoft";
  return "otp";
}

/**
 * Carry email OAuth choices that were skipped on the email step into a later
 * optional account list. The caller supplies only currently offerable tiles,
 * so availability-gated providers such as Apple never appear by accident.
 */
export function unconnectedEmailProviderOptions<
  T extends { provider?: string },
>(options: readonly T[], connectedProviderIds: ReadonlySet<string>): T[] {
  return options.filter(
    (option) =>
      Boolean(option.provider) &&
      !connectedProviderIds.has(option.provider!.toLowerCase()),
  );
}
